// Marker management component

import L from 'leaflet';
import 'leaflet.markercluster';
import { render } from 'preact';
import { CLUSTER_CONFIG } from '../constants';
import { isValidCoordinates, parseCoordinate, pointInPolygon } from '../utils';
import type { ColorManager } from '../utils/colors';
import type { IconFactory } from './icons';
import type { ExtendedMarker, GristRecord, MapConfig } from '../types';
import { MarkerPopup } from './ui/marker';

/**
 * Callback types for marker events
 */
export interface MarkerCallbacks {
  onMarkerClick?: (record: GristRecord, marker: ExtendedMarker) => void;
  onSelectRecord?: (recordId: number) => void;
}

/**
 * Manager for creating and updating map markers
 */
export class MarkerManager {
  private map: L.Map;
  private markers: Map<number, ExtendedMarker> = new Map();
  private markerGroups: Map<string, L.MarkerClusterGroup> = new Map();
  private colorManager: ColorManager;
  private iconFactory: IconFactory;
  private callbacks: MarkerCallbacks;
  private selectedRecordId: number | null = null;

  constructor(
    map: L.Map,
    colorManager: ColorManager,
    iconFactory: IconFactory,
    callbacks: MarkerCallbacks = {}
  ) {
    this.map = map;
    this.colorManager = colorManager;
    this.iconFactory = iconFactory;
    this.callbacks = callbacks;
  }

  /**
   * Update markers from records
   * @param records - Grist records to display
   * @param config - Map configuration
   */
  updateMarkers(records: GristRecord[], config: MapConfig): void {
    const { nameColumn, latColumn, lngColumn, typeColumn, infoColumns } = config;

    if (!latColumn || !lngColumn) {
      this.clearAll();
      return;
    }

    const newMarkerIds = new Set<number>();
    const markersToAdd: ExtendedMarker[] = [];

    records.forEach((record, index) => {
      const lat = parseCoordinate(record[latColumn]);
      const lng = parseCoordinate(record[lngColumn]);

      if (!isValidCoordinates(lat, lng)) return;

      newMarkerIds.add(record.id);
      const typeValue = typeColumn ? String(record[typeColumn] || 'Sans type') : '';

      let marker = this.markers.get(record.id);

      if (!marker) {
        marker = this.createMarker(record, index, {
          lat,
          lng,
          name: nameColumn ? String(record[nameColumn]) : `Point ${index + 1}`,
          type: typeValue,
          infoColumns: infoColumns || [],
        });
        this.markers.set(record.id, marker);
        markersToAdd.push(marker);
      } else {
        marker.markerType = typeValue;
      }

      this.updateMarkerIcon(marker, typeValue);
    });

    this.removeStaleMarkers(newMarkerIds);
    this.addMarkersToGroups(markersToAdd);
    this.fitBoundsIfNeeded(markersToAdd.length === this.markers.size);
  }

  /**
   * Set the selected record
   * @param recordId - Record ID to select, or null to deselect
   */
  setSelectedRecord(recordId: number | null): void {
    const previousId = this.selectedRecordId;
    this.selectedRecordId = recordId;

    // Update previous marker
    if (previousId !== null && previousId !== recordId) {
      const prevMarker = this.markers.get(previousId);
      if (prevMarker) {
        this.updateMarkerIcon(prevMarker, prevMarker.markerType || '');
      }
    }

    // Update new selected marker
    if (recordId !== null) {
      const marker = this.markers.get(recordId);
      if (marker) {
        marker.setIcon(this.iconFactory.getSelectedIcon());
      }
    }
  }

  /**
   * Center map on a record and open its popup
   * @param record - Record to center on
   * @param config - Map configuration
   */
  centerOnRecord(record: GristRecord, config: MapConfig): void {
    const lat = parseCoordinate(record[config.latColumn]);
    const lng = parseCoordinate(record[config.lngColumn]);

    if (!isValidCoordinates(lat, lng)) return;

    this.map.setView([lat, lng], Math.max(this.map.getZoom(), 12));

    const marker = this.markers.get(record.id);
    if (marker?.markerType) {
      const group = this.markerGroups.get(marker.markerType);
      group?.zoomToShowLayer(marker, () => {
        marker.openPopup();
      });
    }
  }

  /**
   * Update all markers of a specific type with new color
   * @param type - Type name
   * @param color - New color
   */
  updateTypeColor(type: string, color: string): void {
    const coloredIcon = this.iconFactory.getColoredIcon(color);

    this.markers.forEach((marker) => {
      if (marker.markerType === type && marker.recordId !== this.selectedRecordId) {
        marker.setIcon(coloredIcon);
      }
    });

    // Recreate the marker group for this type
    this.recreateMarkerGroup(type);
  }

  /**
   * Get all unique types from current markers
   */
  getTypes(): Set<string> {
    const types = new Set<string>();
    this.markers.forEach((marker) => {
      if (marker.markerType) {
        types.add(marker.markerType);
      }
    });
    return types;
  }

  /**
   * Filter markers to show only those inside a GeoJSON geometry.
   * Markers outside the geometry are removed from their cluster groups.
   * @param geometry - GeoJSON geometry to filter by
   */
  filterByPolygon(geometry: GeoJSON.Geometry): void {
    this.markers.forEach((marker) => {
      const latlng = marker.getLatLng();
      const type = marker.markerType || 'default';
      const group = this.markerGroups.get(type);
      if (!group) return;

      const inside = pointInPolygon(latlng.lat, latlng.lng, geometry);
      const groupLayers = new Set(group.getLayers() as L.Layer[]);

      if (!inside && groupLayers.has(marker)) {
        group.removeLayer(marker);
      } else if (inside && !groupLayers.has(marker)) {
        group.addLayer(marker);
      }
    });
  }

  /**
   * Show all markers by re-adding any that were removed from their cluster groups.
   */
  showAllMarkers(): void {
    this.markers.forEach((marker) => {
      const type = marker.markerType || 'default';
      const group = this.getOrCreateMarkerGroup(type);
      const groupLayers = new Set(group.getLayers() as L.Layer[]);
      if (!groupLayers.has(marker)) {
        group.addLayer(marker);
      }
    });
  }

  /**
   * Clear all markers
   */
  clearAll(): void {
    this.markerGroups.forEach((group) => group.clearLayers());
    this.markers.clear();
  }

  /**
   * Get marker by record ID
   */
  getMarker(recordId: number): ExtendedMarker | undefined {
    return this.markers.get(recordId);
  }

  /**
   * Get marker count
   */
  get size(): number {
    return this.markers.size;
  }

  private createMarker(
    record: GristRecord,
    index: number,
    data: {
      lat: number;
      lng: number;
      name: string;
      type: string;
      infoColumns: string[];
    }
  ): ExtendedMarker {
    const marker = L.marker([data.lat, data.lng]) as ExtendedMarker;
    const displayName = data.name || 'Sans nom';

    marker.recordId = record.id;
    marker.recordIndex = index;
    marker.markerType = data.type;

    marker.on('click', () => {
      this.callbacks.onSelectRecord?.(record.id);

      // Lazy popup creation
      if (!marker.getPopup()) {
        const popupContent = this.createPopupContent(record, displayName, data);
        marker.bindPopup(popupContent).openPopup();
      }

      this.callbacks.onMarkerClick?.(record, marker);
    });

    return marker;
  }

  private createPopupContent(
    record: GristRecord,
    name: string,
    data: { lat: number; lng: number; type: string; infoColumns: string[] }
  ): HTMLElement {
    const container = document.createElement('div');

    render(
      <MarkerPopup name={name} type={data.type} record={record} infoColumns={data.infoColumns} />,
      container
    );

    return container;
  }

  private updateMarkerIcon(marker: ExtendedMarker, typeValue: string): void {
    if (marker.recordId === this.selectedRecordId) {
      marker.setIcon(this.iconFactory.getSelectedIcon());
    } else if (typeValue) {
      const color = this.colorManager.getColor(typeValue);
      marker.setIcon(this.iconFactory.getColoredIcon(color));
    } else {
      marker.setIcon(this.iconFactory.getDefaultIcon());
    }
  }

  private removeStaleMarkers(newMarkerIds: Set<number>): void {
    const markersToRemove: ExtendedMarker[] = [];

    this.markers.forEach((marker, id) => {
      if (!newMarkerIds.has(id)) {
        markersToRemove.push(marker);
        this.markers.delete(id);
      }
    });

    if (markersToRemove.length > 0) {
      // Only pass the layers that belong to each group to avoid calling
      // removeLayers with markers that aren't members of a group. Passing
      // markers that don't belong to a group can cause internal cluster
      // state inconsistencies such as reading undefined __parent refs.
      this.markerGroups.forEach((group, type) => {
        // domain check: type must match
        let layersForGroup = markersToRemove.filter((m) => (m.markerType || 'default') === type);

        // safety: ensure the group actually contains the layer
        try {
          const groupLayers = new Set(group.getLayers ? (group.getLayers() as any[]) : []);
          const beforeCount = layersForGroup.length;
          layersForGroup = layersForGroup.filter((m) => groupLayers.has(m));

          if (beforeCount !== layersForGroup.length) {
            // If some markers were filtered out, log debug info for easier troubleshooting
            // (helps detect stale or cross-group markers being attempted removed)
            // eslint-disable-next-line no-console
            console.debug(
              `MarkerManager: filtered ${beforeCount - layersForGroup.length} markers when removing stale markers from group '${type}'.`
            );
          }
        } catch (err) {
          // If getLayers throws for some reason, proceed conservatively and keep the original list.
          // eslint-disable-next-line no-console
          console.warn(
            'MarkerManager: failed to inspect group layers while removing stale markers',
            { type, err }
          );
        }

        if (layersForGroup.length > 0) {
          try {
            group.removeLayers(layersForGroup);
          } catch (err) {
            // Defensive: if removeLayers fails (e.g. cluster internal state mismatch)
            // log full context to aid debugging and continue processing other groups.
            // eslint-disable-next-line no-console
            console.error('MarkerManager: removeLayers failed', {
              type,
              markers: layersForGroup.map((m) => m.recordId),
              err,
            });
          }
        }
      });
    }
  }

  private addMarkersToGroups(markers: ExtendedMarker[]): void {
    markers.forEach((marker) => {
      const type = marker.markerType || 'default';
      const group = this.getOrCreateMarkerGroup(type);
      try {
        // Avoid adding duplicate layers where possible
        const groupLayers = new Set(group.getLayers ? (group.getLayers() as any[]) : []);
        if (!groupLayers.has(marker)) {
          group.addLayer(marker);
        } else {
          // eslint-disable-next-line no-console
          console.debug(
            `MarkerManager: skip addLayer, marker already in group '${type}'`,
            marker.recordId
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('MarkerManager: addLayer failed for group', {
          type,
          markerId: marker.recordId,
          err,
        });
        try {
          // Last resort: attempt addLayer anyway
          group.addLayer(marker);
        } catch (err2) {
          // eslint-disable-next-line no-console
          console.error('MarkerManager: addLayer threw during retry', {
            type,
            markerId: marker.recordId,
            err2,
          });
        }
      }
    });
  }

  private getOrCreateMarkerGroup(type: string): L.MarkerClusterGroup {
    if (!this.markerGroups.has(type)) {
      const color = this.colorManager.getColor(type);
      const group = L.markerClusterGroup({
        ...CLUSTER_CONFIG,
        iconCreateFunction: (cluster) => this.createClusterIcon(cluster, color),
      });

      group.addTo(this.map);
      this.markerGroups.set(type, group);
    }
    return this.markerGroups.get(type)!;
  }

  private createClusterIcon(cluster: L.MarkerCluster, color: string): L.DivIcon {
    const childCount = cluster.getChildCount();
    return new L.DivIcon({
      html: `<div style="background-color: ${color};"><span>${childCount}</span></div>`,
      className: 'marker-cluster',
      iconSize: new L.Point(40, 40),
    });
  }

  private recreateMarkerGroup(type: string): void {
    const group = this.markerGroups.get(type);
    if (!group) return;
    try {
      this.map.removeLayer(group);
    } catch (err) {
      // Defensive - removing a group may fail silently in off cases, log to help debugging
      // eslint-disable-next-line no-console
      console.warn('MarkerManager: map.removeLayer failed for group', { type, err });
    }
    this.markerGroups.delete(type);

    const newGroup = this.getOrCreateMarkerGroup(type);
    this.markers.forEach((marker) => {
      if (marker.markerType === type) {
        try {
          newGroup.addLayer(marker);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('MarkerManager: failed to re-add marker to recreated group', {
            type,
            markerId: marker.recordId,
            err,
          });
        }
      }
    });
  }

  private fitBoundsIfNeeded(isInitialLoad: boolean): void {
    if (this.markers.size === 0 || !isInitialLoad) return;

    const allMarkers: L.Marker[] = [];
    this.markerGroups.forEach((group) => {
      allMarkers.push(...(group.getLayers() as L.Marker[]));
    });

    if (allMarkers.length > 0) {
      const featureGroup = L.featureGroup(allMarkers);
      const bounds = featureGroup.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }
}
